import { useState } from 'react'
import { LayoutChangeEvent, StyleSheet } from 'react-native'
import Animated from 'react-native-reanimated'
import { useAnimatedOpacityOnly } from '@ui/animated/useAnimatedOpacity'

import OvalMask from './components/OvalMask'
import OvalOutlines from './components/OvalOutlines'
import {
  OVAL_VISIBILITY_IN_DURATION,
  OVAL_VISIBILITY_OUT_DURATION,
} from './styles'
import { MeasurementOvalNormalizedFrame, Oval, OvalState } from './types'
import { resolveOvalLayout } from './utils'

type Size = {
  width: number
  height: number
}

/** Kept module-level: `useAnimatedToggle` restarts whenever the config changes. */
const FADE_IN = { timing: { duration: OVAL_VISIBILITY_IN_DURATION } }
const FADE_OUT = { timing: { duration: OVAL_VISIBILITY_OUT_DURATION } }

type Props = {
  state?: OvalState
  /** Multiplier on top of the camera frame or the responsive fallback. */
  scale?: number
  /** Oval bounds normalized to the full camera viewport. */
  normalizedFrame?: MeasurementOvalNormalizedFrame
  /** Fades the whole overlay (mask + outlines) in/out. */
  isVisible?: boolean
}

/**
 * Fullscreen overlay for the eye measurement flow: dims everything outside
 * the oval (gradient mask) and draws two state-aware outlines around it.
 * Renders at the size of its parent — mount it absolutely over the camera feed.
 */
export default function OvalComponent({
  state = Oval.States.initial,
  scale = 1,
  normalizedFrame,
  isVisible = true,
}: Props) {
  const [size, setSize] = useState<Size | null>(null)
  const styleOpacity = useAnimatedOpacityOnly(
    isVisible,
    isVisible ? FADE_IN : FADE_OUT
  )

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout
    setSize({ width, height })
  }

  const layout = size
    ? resolveOvalLayout(size.width, size.height, normalizedFrame, scale)
    : null

  return (
    <Animated.View
      style={[StyleSheet.absoluteFill, styleOpacity]}
      onLayout={handleLayout}
      pointerEvents="none"
    >
      {layout && (
        <>
          <OvalMask layout={layout} />
          <OvalOutlines layout={layout} state={state} />
        </>
      )}
    </Animated.View>
  )
}
